const API_BASE = 'https://bsapi.julian-zhu-w.workers.dev'

interface RequestOptions {
  method?: string
  body?: any
  headers?: Record<string, string>
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const token = localStorage.getItem('beat-auth-token')
  console.log('API request to:', endpoint, 'with token:', token)
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  console.log('Response status:', response.status)
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || 'Request failed')
  }

  return response.json()
}

export const api = {
  auth: {
    verify: (token: string) => request<{ uid: string; email: string }>('/auth/verify', { method: 'POST', body: { token } }),
    register: (token: string, tier?: string) => request<{ uid: string; email: string; tier: string }>('/auth/register', { method: 'POST', body: { token, tier } }),
  },
  
  user: {
    profile: () => request<any>('/user/profile'),
    subscription: (tier: string) => request<{ success: boolean; tier: string }>('/user/subscription', { method: 'PUT', body: { tier } }),
  },
  
  messages: {
    list: (params?: { status?: string; type?: string }) => {
      const searchParams = new URLSearchParams()
      if (params?.status) searchParams.set('status', params.status)
      if (params?.type) searchParams.set('type', params.type)
      const query = searchParams.toString()
      return request<any[]>(`/messages${query ? `?${query}` : ''}`)
    },
    get: (id: number) => request<any>(`/messages/${id}`),
    create: (data: any) => request<{ success: boolean }>('/messages', { method: 'POST', body: data }),
    update: (id: number, data: any) => request<{ success: boolean }>(`/messages/${id}`, { method: 'PUT', body: data }),
    delete: (id: number) => request<{ success: boolean }>(`/messages/${id}`, { method: 'DELETE' }),
    punch: (id: number) => request<{ success: boolean }>(`/messages/${id}/punch`, { method: 'POST' }),
    beat: {
    punchAll: () => request<{ success: boolean }>('/messages/beat/punch', { method: 'POST' }),
    pending: () => request<any[]>('/messages/beat/pending'),
  },
  },
  
  contacts: {
    list: () => request<any[]>('/contacts'),
    create: (data: { name?: string; email?: string; phone?: string }) => request<any>('/contacts', { method: 'POST', body: data }),
    update: (id: number, data: { name?: string; email?: string; phone?: string }) => request<{ success: boolean }>(`/contacts/${id}`, { method: 'PUT', body: data }),
    delete: (id: number) => request<{ success: boolean }>(`/contacts/${id}`, { method: 'DELETE' }),
  },
  
  upload: {
    audio: async (file: File): Promise<{ key: string; url: string }> => {
      const token = localStorage.getItem('beat-auth-token')
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await fetch(`${API_BASE}/upload/audio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      })
      
      if (!response.ok) throw new Error('Upload failed')
      return response.json()
    },
    
    file: async (file: File): Promise<{ key: string; url: string }> => {
      const token = localStorage.getItem('beat-auth-token')
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await fetch(`${API_BASE}/upload/file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      })
      
      if (!response.ok) throw new Error('Upload failed')
      return response.json()
    },
  },
  
  stripe: {
    checkout: (tier: string) => request<{ url: string }>('/stripe/checkout', { method: 'POST', body: { tier } }),
    portal: () => request<{ url: string }>('/stripe/portal'),
  },
  
  analytics: () => request<any>('/analytics'),
}
